import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { isAuthenticated, getCurrentUser } from '../lib/auth';

/**
 * RouteGuard — protects routes from unauthenticated access.
 *
 * Props:
 *   children — the protected component
 *   role     — (optional) required role string, for future multi-user expansion
 *
 * Behavior:
 *   - No token → redirect to /login (preserves intended destination in ?from=)
 *   - Token present → render children
 *   - role prop is accepted but not enforced yet (single-user auth)
 */
export default function RouteGuard({ children, role }) {
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [authorized, setAuthorized] = useState(false);

  useEffect(() => {
    (async () => {
      if (!isAuthenticated()) {
        setAuthorized(false);
        setChecking(false);
        return;
      }

      // Validate token with /auth/me endpoint
      const user = await getCurrentUser();
      if (user) {
        // Future: check role here when multi-user is implemented
        // if (role && user.role !== role) { setAuthorized(false); ... }
        setAuthorized(true);
      } else {
        setAuthorized(false);
      }
      setChecking(false);
    })();
  }, [role]);

  // Loading state — minimal spinner
  if (checking) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: '#e5e4df' }}
      >
        <div
          className="w-6 h-6 border-2 border-t-transparent"
          style={{
            borderColor: '#c9a84c',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Not authenticated → redirect to login
  if (!authorized) {
    const from = location.pathname !== '/' ? `?from=${encodeURIComponent(location.pathname)}` : '';
    return <Navigate to={`/login${from}`} replace />;
  }

  return children;
}
